import { useQuery } from '@tanstack/react-query';

/**
 * Supported country / entity-type catalogue for new engagements (ENT-284).
 *
 * The create wizard drives its Country + Entity type selectors from this hook so
 * only combinations the backend can actually register are offered (entity types
 * are *dependent* on the chosen country).
 *
 * ⚠️ BACKEND-DRIVEN (pending): there is no query yet that lists supported
 * engagement definitions — `supportedCountries` / `supportedEntityTypes` exist
 * but are platform-wide and not country-dependent, and `activeDefinition` only
 * resolves a single (country, entityType) pair. Until the BE exposes
 * `supportedEngagementDefinitions`, this returns a static catalogue. Swap the
 * `queryFn` body for the real call when it lands — the shape below is the target.
 *
 * See docs/handover/be/ENT-284-india-engagement-ui/supported-engagement-definitions.md.
 *
 * ⚠️ UAE entries (research doc "Gate 0"): `AE_FZ_LLC_IFZA` / `_DMCC` / `_MEYDAN`
 * are listed here ahead of backend approval, by design — this unblocks wiring
 * and testing the wizard against them locally. Each is still `DRAFT` on the
 * backend as of this writing; `activeDefinition(countryCode, entityType)` will
 * return null (and engagement creation will fail server-side) until someone
 * with the ADMIN role runs `submitForReview` → `approveDefinition` for it. If a
 * user reaches this dropdown in a deployed environment before that happens,
 * selecting a UAE entity type will visibly fail at submit, not silently.
 */

export interface EngagementCountry {
  code: string;
  label: string;
  /** Emoji flag for display; optional. */
  flag?: string;
}

export interface EntityTypeOption {
  value: string;
  label: string;
}

/** Composite value stored by the form-engine `engagementJurisdiction` custom field. */
export interface JurisdictionValue {
  countryCode: string;
  entityType: string;
}

export interface SupportedDefinitions {
  countries: EngagementCountry[];
  /** Entity types keyed by country code — the dependent population source. */
  entityTypesByCountry: Record<string, EntityTypeOption[]>;
}

/**
 * Static stand-in for the not-yet-built `supportedEngagementDefinitions` query.
 * Replace with the BE response once it lands (see file header).
 *
 * Exported (not module-private) so callers needing a flat country/entity-type
 * list — e.g. the engagements list page's filter config — derive from this
 * SAME source rather than keeping their own copy that can drift out of sync
 * (ENT-335: the list page's filter had its own hardcoded India-only list here,
 * which is exactly the kind of duplication this export prevents).
 */
export const STATIC_CATALOGUE: SupportedDefinitions = {
  countries: [
    { code: 'IN', label: 'India', flag: '🇮🇳' },
    { code: 'AE', label: 'United Arab Emirates', flag: '🇦🇪' },
  ],
  entityTypesByCountry: {
    IN: [{ value: 'PRIVATE_LIMITED', label: 'Private Limited Company' }],
    // Free Zone LLC — one entity type per free-zone authority (they have
    // materially different steps, costs, and timelines; see the backend
    // research doc). Still DRAFT — see the ⚠️ note above.
    AE: [
      { value: 'AE_FZ_LLC_IFZA', label: 'Free Zone LLC — IFZA' },
      { value: 'AE_FZ_LLC_DMCC', label: 'Free Zone LLC — DMCC' },
      { value: 'AE_FZ_LLC_MEYDAN', label: 'Free Zone LLC — Meydan' },
    ],
  },
};

export interface UseSupportedDefinitionsResult {
  isLoading: boolean;
  countries: EngagementCountry[];
  entityTypesByCountry: Record<string, EntityTypeOption[]>;
  /** Entity types for a given country code (empty if the country is unknown). */
  entityTypesFor: (countryCode: string) => EntityTypeOption[];
}

export function useSupportedDefinitions(): UseSupportedDefinitionsResult {
  const query = useQuery({
    queryKey: ['supportedEngagementDefinitions'],
    // Pending BE: swap to fetchGql(SupportedEngagementDefinitionsDocument) once the
    // query lands (see docs/handover/be/ENT-284 + the file header note).
    queryFn: async (): Promise<SupportedDefinitions> => STATIC_CATALOGUE,
    staleTime: Infinity,
  });

  const data = query.data ?? STATIC_CATALOGUE;

  return {
    isLoading: query.isLoading,
    countries: data.countries,
    entityTypesByCountry: data.entityTypesByCountry,
    entityTypesFor: (countryCode: string) => data.entityTypesByCountry[countryCode] ?? [],
  };
}
